<template>
  <div class="recom-card">
    <span class="title">{{ content.cardContent }}</span>
    <div class="divider"></div>
    <!-- 问题列表 -->
    <div class="question-list">
      <div v-for="(item, index) in pagedQuestions" :key="index" class="question-row" @click="handleClickQuestion(item)">
        <span class="questions">{{ item }}</span>
        <svg class="row-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M6 4L10 8L6 12" stroke="#BBBEBC" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
    </div>
    <div class="divider"></div>
    <!-- 分页 -->
    <div class="pagination">
      <div class="page-arrow" :class="{ disabled: currentPage === 1 }" @click="prevPage">
        <ArrowIcon />
      </div>
      <span v-for="page in totalPages" :key="page" class="page-number" :class="{ active: page === currentPage }"
        @click="goToPage(page)">{{ page }}</span>
      <div class="page-arrow right" :class="{ disabled: currentPage === totalPages }" @click="nextPage">
        <ArrowIcon />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useChatStore } from "../../../store/chat";
import { MessageType } from "../../../enum/chatEnum";
import { useLang } from "../../../hooks/useLang";
import dayjs from "dayjs";
import ArrowIcon from "./ArrowIcon.vue";

const PAGE_SIZE = 5;

export default defineComponent({
  name: "RecommendationCard",
  components: { ArrowIcon },
  props: {
    content: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const cStore = useChatStore();
    const { getUCLang } = useLang();
    const currentTime = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const currentPage = ref(1);
    const totalPages = computed(() =>
      Math.max(1, Math.ceil((props.content.questions?.length ?? 0) / PAGE_SIZE))
    );
    const pagedQuestions = computed(() => {
      const list: string[] = props.content.questions ?? [];
      const start = (currentPage.value - 1) * PAGE_SIZE;
      return list.slice(start, start + PAGE_SIZE);
    });

    const goToPage = (page: number) => {
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
    };
    const prevPage = () => goToPage(currentPage.value - 1);
    const nextPage = () => goToPage(currentPage.value + 1);

    const handleClickQuestion = (message: string) => {
      const query = {
        chatId: cStore.chatId,
        messageType: MessageType.TEXT,
        language: getUCLang(),
        contentType: "text/plain",
        content: message,
        timestamp: currentTime,
      };

      const thisChat = {
        chatId: cStore.chatId,
        avatar: "1", // 网页渠道客户默认头像
        content: message,
        contentType: "text/plain",
        sequenceId: "unknown",
        timestamp: currentTime,
        sender: "custom",
        username: "custom",
      };

      cStore.chatInfo.push(thisChat);

      cStore.wsSend(query);
    };

    return {
      handleClickQuestion,
      currentPage,
      totalPages,
      pagedQuestions,
      goToPage,
      prevPage,
      nextPage,
    };
  },
});
</script>

<style lang="less" scoped>
.recom-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #5f6260;
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &>.question-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    cursor: pointer;

    &>.questions {
      flex: 1;
      min-width: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      color: #2e9b4a;
      word-break: break-word;
    }

    &>.row-arrow {
      flex-shrink: 0;
    }
  }
}

.divider {
  border-top: 1px solid #f2f2f2;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  user-select: none;

  .page-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    cursor: pointer;

    &.right svg {
      transform: rotate(180deg);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }

  .page-number {
    font-size: 12px;
    line-height: 20px;
    color: #616961;
    cursor: pointer;
    padding: 0 2px;

    &.active {
      color: #2e9b4a;
      font-weight: 500;
    }
  }
}
</style>
